'use client';
import {styled} from '@mui/material/styles';
import {useEffect, useRef, useState, type RefObject} from 'react';
import {headerOverlayProps} from '../header/headerOverlay';
import {HERO_HEIGHT_VAR, HERO_OVERHANG_VAR, heroFrame} from './heroSize';
import {HeroStage} from './HeroStage';
import {HeroMode, type HeroShellProps, type HeroSize} from './types';

const DIM_VAR = '--hero-dim';
// The hero is fully black by the time the curtain has covered this much of it.
const DIM_RANGE_RATIO = 0.85;
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const HIDDEN = 'none';

// The pin needs an ancestor chain free of `overflow: hidden`. The negative margin is
// the overhang the curtain rests over, so the hero costs the page its height minus
// that strip — it must be read from the same var, in the same units, or the pin is
// wrong wherever `svh` and `vh` disagree (every mobile browser).
const Root = styled('section', {shouldForwardProp: propName => propName !== 'oSize'})<{oSize: HeroSize}>(({theme, oSize}) => ({
  ...heroFrame(theme, HeroMode.PinnedCurtain, oSize),
  position: 'sticky',
  top: 0,
  height: `var(${HERO_HEIGHT_VAR})`,
  paddingBottom: `var(${HERO_OVERHANG_VAR})`,
  marginBottom: `calc(-1 * var(${HERO_OVERHANG_VAR}))`,
}));

const Dim = styled('div')(({theme}) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  pointerEvents: 'none',
  background: theme.palette.colors.base.black,
  opacity: `var(${DIM_VAR}, 0)`,
  [`@media ${REDUCED_MOTION}`]: {display: HIDDEN},
}));

// How much of the hero the curtain still leaves showing, measured against the curtain
// itself rather than the document, so a hero that is not the first section dims and
// mutes on its own geometry. A sticky element's offsetTop tracks where it is *stuck*,
// never where it started, so it cannot be used for this.
function useHeroScroll(rootRef: RefObject<HTMLElement | null>) {
  const [covered, setCovered] = useState(false);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    const reducedMotion = window.matchMedia(REDUCED_MOTION);
    let frame = 0;
    let paintedProgress = -1;
    let paintedCovered: boolean | null = null;
    const paint = () => {
      frame = 0;
      const {top, height} = root.getBoundingClientRect();
      // The overhang is the hero's own bottom padding, so what the page gives up to the
      // hero — and what the curtain has to travel to cover it — is the rest of it.
      const travel = height - parseFloat(getComputedStyle(root).paddingBottom);
      const curtain = root.nextElementSibling;
      const showing = curtain ? curtain.getBoundingClientRect().top - top : travel - window.scrollY;
      const progress = Math.min(1, Math.max(0, (1 - showing / travel) / DIM_RANGE_RATIO));
      // The dim saturates before the curtain finishes covering the hero, so coverage has
      // to be compared on its own — not skipped because the dim stopped changing.
      const isCovered = showing <= 0;
      if (progress === paintedProgress && isCovered === paintedCovered) {
        return;
      }
      paintedProgress = progress;
      paintedCovered = isCovered;
      setCovered(isCovered);
      root.style.setProperty(DIM_VAR, reducedMotion.matches ? '0' : String(progress));
    };
    const schedule = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(paint);
      }
    };
    paint();
    window.addEventListener('scroll', schedule, {passive: true});
    window.addEventListener('resize', schedule);
    reducedMotion.addEventListener('change', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      reducedMotion.removeEventListener('change', schedule);
      window.cancelAnimationFrame(frame);
      root.style.removeProperty(DIM_VAR);
    };
  }, [rootRef]);
  return covered;
}

export function HeroPinned({size, headerOverlay = true, ...content}: HeroShellProps) {
  const rootRef = useRef<HTMLElement>(null);
  const covered = useHeroScroll(rootRef);

  // Once the curtain covers it the hero is not on screen, so its controls leave the
  // tab order with it — otherwise tabbing into them focuses something nobody can see.
  return (
    <Root ref={rootRef} oSize={size} inert={covered || undefined} {...(headerOverlay ? headerOverlayProps : {})}>
      <HeroStage {...content} inView={!covered} />
      <Dim />
    </Root>
  );
}

HeroPinned.displayName = 'HeroPinned';
